import { ensureArray } from '@/lib/utils/ensure-array';
import {
  DEFAULT_FORM_VALUES,
  type PatchListRow,
  type TemplateId,
  type VoiceCampaignFormValues,
} from './types';

/* ------------------------------------------------------------------ */
/* Permissive readers                                                  */
/* ------------------------------------------------------------------ */

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

const toCalcuttaTime = (date?: string | null) => {
  if (!date) return '';

  return `${date.replace(' ', 'T')}+05:30`;
};

const num = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const asObject = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const json = <T>(v: unknown, fallback: T): T => {
  if (typeof v !== 'string' || !v.trim()) return fallback;
  try {
    return JSON.parse(v) as T;
  } catch {
    return fallback;
  }
};

/* ------------------------------------------------------------------ */
/* Child-collection mappers                                            */
/* ------------------------------------------------------------------ */

/** campaignPrompts rows → category → promptId ("welcome" → "457"). */
function promptIndex(rows: unknown[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows) {
    const o = asObject(row);
    if (!o) continue;
    const category = str(o.promptCategory).trim().toLowerCase();
    const id = str(o.promptId);
    if (category && id && !map.has(category)) map.set(category, id);
  }
  return map;
}

/**
 * campaignSms row → the JSON blob the compose form keeps per SMS event:
 * `{ url, requestType, header, smsText | payload }`. On the wire, GET stores
 * the message in `smsText` and POST in `payload`; the DB row keeps the URL
 * in `sms_text` and the message in `sms_message` — swap accordingly.
 */
function smsApiJson(row: Record<string, unknown>): string {
  return String(row.smsWebhookId ?? '');
}

function smsIndex(rows: unknown[]): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const o = asObject(row);
    if (!o) continue;
    const webhookId = str(o.smsWebhookId).trim();
    if (!webhookId) continue;
    const event = str(o.smsEvent).trim().toUpperCase();
    if (event && !map.has(event)) {
      map.set(event, o);
    }
  }
  return map;
}

/* ------------------------------------------------------------------ */
/* Hydration                                                           */
/* ------------------------------------------------------------------ */

export function hydrateFromClonePayload(
  raw: unknown,
  isContinuous: boolean,
): Partial<VoiceCampaignFormValues> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;

  /* Everything user-editable lives under `campaign`; child collections sit
   * beside it. Older flat payloads fall back to reading from `r` itself. */
  const c = asObject(r.campaign) ?? r;
  const s = asObject(r.scheduleList) ?? r;

  /* ---------- template ---------- */
  const continous = c.continuous;
  const innerTemplate = ['7', '8', '9'];
  const hasTempate10 = continous ? innerTemplate.includes(String(c.templateId)) : false;
  const rawTemplateId = str(hasTempate10 ? '10' : String(c.templateId));
  const ALLOWED: readonly string[] = ['0', '1', '2', '3', '7', '8', '9', '10'];
  const templateId: TemplateId = ALLOWED.includes(rawTemplateId)
    ? (rawTemplateId as TemplateId)
    : '0';

  /* ---------- prompts (category map, legacy flat fields as fallback) ---------- */
  const prompts = promptIndex(asArray(r.campaignPrompts));
  const pid = (category: string, legacyKey: string): string =>
    prompts.get(category) || str(c[legacyKey] ?? r[legacyKey]);

  const welcomeId = pid('welcome', 'welcomePId');
  const menuId = pid('menu', 'menuPId');
  const noinputId = pid('noinput', 'noInputPId');
  const wronginputId = pid('wronginput', 'wrongInputPId');
  const thanksId = pid('thanks', 'thanksPId');
  const noAgentId = pid('noagent', 'noAgentId');

  /* ---------- SMS events ---------- */
  const smsRows = asArray(r.campaignSms);
  const sms = smsIndex(smsRows);
  const smsJsonFor = (event: string): string => {
    const row = sms.get(event);
    return row ? smsApiJson(row) : '';
  };
  // callDurationSMS is one shared field on the wire; take the first row that has one.
  let callDurationSMS = 0;
  for (const row of smsRows) {
    const o = asObject(row);
    const v = o?.callDurationSMS;
    if (o && v !== undefined && v !== null && v !== '') {
      callDurationSMS = num(v, 0);
      break;
    }
  }

  const webhookId =
    str(c.webhookId ?? r.webhookId) ||
    str(smsRows.map(asObject).find((o) => o && str(o.webhookId) !== '')?.webhookId ?? '');

  /* ---------- IVR ---------- */
  const ivrRow = asArray(r.ivrList)
    .map(asObject)
    .find((o) => o !== null);
  const ivrId = isContinuous || templateId === '3' ? str(ivrRow?.ivrId ?? c.ivrId ?? r.ivrId) : '';
  /* ---------- CLIs / locations ---------- */
  const selectedCLI = asArray(r.campaignClis)
    .map((row) => {
      const o = asObject(row);
      return o ? str(o.cli ?? o.cliNumber ?? o.cliId) : str(row);
    })
    .filter(Boolean);

  const locationIds = ensureArray(r.locationList as { locationId: number }[]).map((r) =>
    String(r.locationId),
  );

  /* ---------- patch list (agents) ---------- */
  const agentParsed = json<{ patchList?: PatchListRow[] } | PatchListRow[]>(r.agentRows, []);
  const legacyPatchList = Array.isArray(agentParsed)
    ? agentParsed
    : Array.isArray(agentParsed.patchList)
      ? agentParsed.patchList
      : [];
  const patchList = (
    asArray(r.campaignAgents).length > 0 ? asArray(r.campaignAgents) : legacyPatchList
  ) as PatchListRow[];

  /* ---------- DTMF ---------- */
  const dtmf = (() => {
    const v = c.dtmf ?? r.dtmf;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.trim()) {
      return v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  })();

  const retryIntervalSec = num(c.retryInterval ?? r.retryInterval, 0);

  const patch: Partial<VoiceCampaignFormValues> = {
    campaignName: str(c.campaignName),
    templateId,
    sendLater: Boolean(c.scheduleTime ?? r.scheduleTime),
    scheduleDate: str(c.scheduleTime ?? r.scheduleTime),
    retries: num(c.retries ?? r.retries, 0),
    retryInterval: Math.round(retryIntervalSec / 60),
    ringDuration: num(c.ringDuration ?? r.ringDuration, 0) || 10,

    baseFileMode: (c.baseId ?? r.baseId) ? 'oldBaseFile' : DEFAULT_FORM_VALUES.baseFileMode,
    oldBaseId: (c.baseId ?? r.baseId) ? str(c.baseId ?? r.baseId) : '',

    /* Toggle flags derived from presence of the saved IDs. */
    useWelcomeFile: Boolean(welcomeId),
    useNoinputFile: Boolean(noinputId),
    useWronginputFile: Boolean(wronginputId),
    useThanksFile: Boolean(thanksId),

    welcomeId,
    menuId,
    noinputId,
    wronginputId,
    thanksId,
    rePrompt: str(c.rePrompt ?? r.rePrompt),
    repeatMenu: str(c.repeatMenu ?? r.repeatMenu),
    menuWaitTime: str(c.menuWaitTime ?? r.menuWaitTime),
    dtmf,
    patchList,
    noAgentId,
    ivrId,
    selectedTTSFlowId: str(c.ttsFlowId ?? r.ttsFlowId),

    locationIds,
    selectedCLI,

    sendSmsSuccess: sms.has('CALL_CONNECTED'),
    smsApiSuccess: smsJsonFor('CALL_CONNECTED'),
    callDurationSMS,
    sendSmsFail: sms.has('CALL_FAILED'),
    smsApiFail: smsJsonFor('CALL_FAILED'),
    sendSmsDtmf: sms.has('VALID_DTMF'),
    smsApiDtmf: smsJsonFor('VALID_DTMF'),
    sendSmsPatchSuccess: sms.has('PATCH_CONNECTED'),
    callPatchSuccessMessage: smsJsonFor('PATCH_CONNECTED'),
    sendSmsPatchFail: sms.has('PATCH_FAILED'),
    callPatchFailMessage: smsJsonFor('PATCH_FAILED'),
    sendWebhook: Boolean(webhookId),
    webhookId,

    startDate: isContinuous ? str(c.startDate) : '',
    endDate: isContinuous ? str(c.endDate) : '',
    // Backend ships HH:mm:ss — <input type="time"> wants HH:mm.
    continuousSchedule: isContinuous ? str(s.scheduleJson) : '',
    continueWithBase: isContinuous ? Boolean(c.continueWithBase) : false,
    useIvr: isContinuous ? Boolean(ivrId) : false,
    templateIdByTTS: continous ? String(c.templateId) : '',
  };

  return patch;
}
